import "./QuickWhatIfPanelComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit, ITimeoutService} from "angular";
import {ComponentCallback} from "../../../types/AngularJS";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {StringKeyed} from "../../../types/GenericTypes";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {StrategyService, StrategySummary} from "../../../types/StrategyService";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {StrategyEquityTrade, StrategyTrade} from "../../../strategybuilder/models/Strategy";
import {Position} from "../../../types/Portfolio";
import {QuickWhatIfService} from "../../service/QuickWhatIfService";
import {Subscription} from "rxjs/Subscription";
import {CompanyInfo} from "../../../types/Company";
import {IModalService} from "angular-ui-bootstrap";
import {PortfolioPositions} from "../../models/Portfolio";
import {FeatureNames, UserConfig} from "../../../types/UserConfig";

export const QuickWhatIf = "quickWhatIf";

export function quickWhatIf(): IComponentOptions {
    return {
        templateUrl: "app/portfolio/sidebar/quick-what-if/QuickWhatIfPanelComponent.html",
        bindings: {
            portfolio: "<",
            quickWhatIfService: "<",
            onTrade: "&"
        },
        controllerAs: "ctrl",
        controller: QuickWhatIfPanelComponentController
    };
}

export class QuickWhatIfPanelComponentController implements IOnInit, IOnDestroy {
    public portfolio: PortfolioPositions;
    public quickWhatIfService: QuickWhatIfService;
    public onTrade: ComponentCallback;

    public selectedCompany?: CompanyInfo;
    public hasStrategyBuilder: boolean;
    public newPosition: Position | null = null;
    public positions: Array<Position> = [];
    public gcd: number;
    public netTheo: number;

    private quickWhatIfSubscription: Subscription = Subscription.EMPTY;

    /** @ngInject */
    public constructor(public currentUserService: CurrentUserService,
                       private strategyService: StrategyService,
                       private optionService: any,
                       private $uibModal: IModalService,
                       private $timeout: ITimeoutService,
                       public userConfig: UserConfig) {
    }

    public $onInit(): void {
        this.hasStrategyBuilder = this.currentUserService.hasFeature(FeatureNames.SCREEN_STRATEGY_BUILDER);
        this.quickWhatIfSubscription = this.quickWhatIfService.subscribe(p => this.onPositionsChanged(p));
    }

    private async onPositionsChanged(positions: Array<Position>) {
        const copy = ArrayUtil.clone(positions);
        const whatIf = this.buildWhatIf(copy);
        const details = await this.buildDetailedPositions(whatIf);
        this.$timeout(() => this.positions = details);
    }

    public companySelected(company: CompanyInfo) {
        this.$timeout(() => this.selectedCompany = company);
    }

    public openMobilePicker() {
        const modalInstance = this.$uibModal.open({
            templateUrl: "app/portfolio/templates/mobileWhatIf/mobileWhatIf.tpl.html",
            controller: "optionPickerCtrl",
            size: "fs",
            resolve: {
                stock: () => this.selectedCompany!
            }
        });

        modalInstance.result.then((selectedPositions: Array<Position>) => {
            this.quickWhatIfService.add(...ArrayUtil.clone(selectedPositions));
        });
    }

    public underlyingSelected(underlying: string) {
        const position = {
            code: underlying,
            quantity: 1
        };
        this.quickWhatIfService.add(position);
    }

    public instrumentSelected() {
        if (this.newPosition != null) {
            const position = {
                code: this.newPosition.code,
                quantity: 1
            };
            this.quickWhatIfService.add(position);
            this.newPosition = null;
        }
    }

    private buildWhatIf(positions: Array<Position>): StringKeyed<any> {
        const trades = ArrayUtil.clone(positions);
        const equityTradeDetails = this.positions.filter(pos => pos.code.length === 3);
        const tradeDetails = this.positions.filter(pos => pos.code.length > 3);
        return {
            trades,
            tradeDetails,
            equityTradeDetails,
            scaleFactor: 1
        };
    }

    private async buildDetailedPositions(whatIf: StringKeyed<any>) {
        const request = ObjectUtil.copy(whatIf) as StrategySummary;
        const result = await this.strategyService.strategyResults(request);
        const positions = whatIf.trades;
        const trades = result.strategy.tradeDetails;
        const equityTrades = result.strategy.equityTradeDetails;
        this.gcd = result.gcd;
        this.netTheo = result.netTheo;
        return this.mergeResults(positions, trades, equityTrades);
    }

    private mergeResults(positions: Array<Position>,
                         trades: Array<StrategyTrade>,
                         equityTrades: Array<StrategyEquityTrade>): Array<Position> {
        return positions.map(pos => {
            const equityTrade = equityTrades.find(tr => tr.underlyingCode === pos.code);
            const optionTrade = trades.find(tr => tr.code === pos.code);

            return Object.assign(pos, optionTrade || equityTrade || {});
        });
    }

    public whatIfChanged() {
        const positions = ArrayUtil.clone(this.positions);
        this.quickWhatIfService.next(positions);
    }

    public removePosition(position: Position) {
        this.quickWhatIfService.remove(position);
    }

    public async nextStrike(position: Position) {
        const updated = await this.optionService.nextStrike(position, this.positions);
        this.quickWhatIfService.replace(position, updated);
    }

    public async previousStrike(position: Position) {
        const updated = await this.optionService.previousStrike(position, this.positions);
        this.quickWhatIfService.replace(position, updated);
    }

    public async nextExpiry(position: Position) {
        const updated = await this.optionService.nextExpiry(position, this.positions);
        this.quickWhatIfService.replace(position, updated);
    }

    public async previousExpiry(position: Position) {
        const updated = await this.optionService.previousExpiry(position, this.positions);
        this.quickWhatIfService.replace(position, updated);
    }

    public async rollPosition(position: Position) {
        const rolled = await this.optionService.rollPosition(position);
        this.quickWhatIfService.add(rolled);
    }

    public clear() {
        this.quickWhatIfService.clear();
    }

    public trade() {
        const whatIf = this.buildWhatIf(this.positions);
        this.onTrade({whatIf});
    }

    public $onDestroy(): void {
        this.quickWhatIfSubscription.unsubscribe();
    }
}
